import React, { FC, ReactNode, useContext, useEffect, useMemo } from "react";
import {
  Link,
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import { routes } from "@app/routing";
import { ResultCodes } from "@app/service/resultCodes";
import ResetPassword from "./resetPassword";
import ResetPasswordError from "./resetPasswordError";
import { Auth, LoginRedirect } from "./auth";
import Activation from "./activation";
import PromoLanding from "./promoLanding";
import Main from "./main";
import {
  ConfigContext,
  IntlContext,
  NotificationContext,
  PatientEducationProvider,
  PatientEducationHeapProvider,
  RedirectionContext,
  RoutingType,
} from "@app/provider";
import SetPassword from "./setPassword";
import SendEmail from "./sendEmail";
import Unlock from "./unlock";
import UpdateEmailError from "./updateEmailError";
import ScheduleBloodDraw from "./scheduleBloodDraw";
import acknowledgements from "@etc/acknowledgements.json";
import { useTokenizedLinks } from "@app/hooks";
import { LinkHeap } from "@app/components";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import {
  BillingEstimate,
  NonCoveredService,
  OutOfNetwork,
  PriorAuthorization,
  GuestLogin,
  NoLongerAvailable,
  PostbillGuestLogin,
  PostbillBillingCenter,
  ReviewBills,
  BillingPayment,
  BillingFailure,
  BillingConfirmation,
  InsuranceNoLongerAvailable,
  BillingPaymentPlanDetail,
  BillingUpdatePayment,
  BillingPaymentSuccess,
  UPSGuestLoginPage,
  UPSUpdatePaymentMethodPage,
  UPSPaymentConfirmation,
  UPSReviewInvoice,
  UPSCreatePaymentMethod,
  UPSCreatePaymentConfirmation,
  UPSCreatePaymentFailure,
  UPSNoLongerAvailable,
  UPSReceipt,
} from "@app/pages";
import RenewalInterested from "@app/pages/public/renewalInterested";
import {
  LandingPage as PatientEducationLanding,
  VideoPage as PatientEducationVideoPage,
  QuestionnairePage as PatientEducationQuestionnaire,
  ThanksPage as PatientEducationThanks,
} from "./patientEducation";
import BillingStatementPdf from "@app/postBill/pages/private/billingStatementPdf";
import { RequireUpsSession } from "@app/ups/upsWrapper";
import RecaptchaProvider from "@app/provider/recaptcha";
import PublicGisEntry from "./publicGisEntry";
import PublicGisOrderLookup from "./publicGisOrderLookup";
import GuestGisIntakeLayout from "./publicGisOrderLookup/guestIntake/GuestGisIntakeLayout";
import GuestIntakeFormRouter from "./publicGisOrderLookup/guestIntake/GuestIntakeFormRouter";
import GuestUnderstandingResults from "./publicGisOrderLookup/guestIntake/GuestUnderstandingResults";
import ScheduleSessionPage from "@app/pages/private/scheduleAGeneticInformationSession/scheduleSessionPage";
import PatientEducationPage from "@app/pages/private/scheduleAGeneticInformationSession/patientEducationPage";

const messages = defineMessages({
  publicTokenIsInvalid: {
    id: "publicTokenIsInvalid",
    defaultMessage: "This link is broken.",
  },
  publicTokenIsExpired: {
    id: "publicTokenIsExpired",
    defaultMessage: "This link has expired.",
  },
  publicAccountAlreadyActivated: {
    id: "publicAccountAlreadyActivated",
    defaultMessage: "Your account has already been created.",
  },
  publicAccountSuccessfullyActivated: {
    id: "publicAccountSuccessfullyActivated",
    defaultMessage: "Account was successfully activated",
  },
  publicAccountDeleted: {
    id: "publicAccountDeleted",
    defaultMessage: "Your account has been deleted.",
  },
  publicUpdateEmailNotificationTitle: {
    id: "publicUpdateEmailNotificationTitle",
    defaultMessage: "Email Verified",
  },
  publicUpdateEmailNotification: {
    id: "publicUpdateEmailNotification",
    defaultMessage:
      "Email successfully updated. Please log in with your updated email address.",
  },
  publicResetPasswordNotificationTitle: {
    id: "publicResetPasswordNotificationTitle",
    defaultMessage: "Password Updated",
  },
  publicResetPasswordNotification: {
    id: "publicResetPasswordNotification",
    defaultMessage:
      "Your password has been updated.\nRemember to save your new password.",
  },
  publicContactSupport: {
    id: "publicContactSupport",
    defaultMessage: "Contact support",
  },
  publicLogin: {
    id: "publicLogin",
    defaultMessage: "Log in",
  },
  publicOr: {
    id: "publicOr",
    defaultMessage: "or",
  },
});

// Signup, activation, forgot-password, and unlock routes call executeV3, so
// the reCAPTCHA provider (and Google's v3 script/badge) is mounted only on
// these routes instead of app-wide. Public MP hides the v3 badge on non-confirmation
// steps via CSS (see drawRequestRecaptcha.scss). signUpInvite is included
// because the SignUp component also renders on /sign_up/:invite?. matchPath
// (not includes) is required for the param route. Entering the whitelist
// mounts a fresh grecaptcha instance; navigating signup -> activation (or
// resetPassword -> sendEmail) keeps it mounted; leaving unmounts it.
// NOTE: read `routes` lazily at render (not module-eval) — `@app/routing` is
// part of an import cycle and isn't initialized when this module is evaluated.
export const RecaptchaRouteGate: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const recaptchaRoutes = [
    routes.signUp,
    routes.signUpInvite,
    routes.activation,
    routes.resetPassword,
    routes.sendEmail,
    routes.unlock,
    routes.drawRequest,
  ];
  const enabled = recaptchaRoutes.some((route) => matchPath(route, pathname));
  return enabled ? (
    <RecaptchaProvider>{children}</RecaptchaProvider>
  ) : (
    <>{children}</>
  );
};

const PublicRouting: FC = () => {
  const intl = useIntl();

  const location = useLocation();
  const navigationType = useNavigationType();
  const { search } = useLocation();

  useTokenizedLinks();

  const { notifications, addNotification, clear } =
    useContext(NotificationContext);
  const { currentLanguage } = useContext(IntlContext);
  const { config } = useContext(ConfigContext);

  const { setRedirection, redirectionTrigger } = useContext(RedirectionContext);

  const redirectEducation = config.patientPortal.redirectEducation ?? true;

  // Handle old /education/{id} routes (but NOT /education/prenatal/*)
  const educationRouteRegex = /^\/education\/([^/]+)/;
  const match = educationRouteRegex.exec(location.pathname);
  const isOldEducationRoute = match && match[1] !== "prenatal";

  useEffect(() => {
    setRedirection(RoutingType.PUBLIC);
  }, []);

  useEffect(() => {
    if (redirectionTrigger === "email_verified") {
      addNotification({
        title: intl.formatMessage(messages.publicUpdateEmailNotificationTitle),
        message: intl.formatMessage(messages.publicUpdateEmailNotification),
        type: "success",
      });
    }
  }, [redirectionTrigger]);

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  useEffect(() => {
    return () => {
      if (navigationType === "PUSH") {
        clear();
      }
    };
  }, [location, navigationType]);

  useEffect(() => {
    const code = searchParams.get("code");
    if (code) {
      clear();
      switch (code) {
        case ResultCodes.TOKEN_EXPIRED_ERROR: {
          addNotification({
            message: intl.formatMessage(messages.publicTokenIsExpired),
            type: "error",
          });
          break;
        }
        case ResultCodes.TOKEN_INVALID_SIGNATURE_ERROR: {
          addNotification({
            message: intl.formatMessage(messages.publicTokenIsInvalid),
            actions: (
              <>
                <Link className="link" to={routes.logIn}>
                  {intl.formatMessage(messages.publicLogin)}
                </Link>{" "}
                {intl.formatMessage(messages.publicOr)}{" "}
                <LinkHeap
                  target="_blank"
                  key="privacyPolicy"
                  rel="noreferrer"
                  to={acknowledgements.links.contactUs}
                  heapEventName={HEAP_EVENTS.upp_click_contactnatera}
                  heapEventProps={{
                    location: HeapEventLocation.broken_link_error,
                  }}
                >
                  {intl.formatMessage(messages.publicContactSupport)}
                </LinkHeap>
              </>
            ),
            type: "error",
          });

          break;
        }
        case ResultCodes.ACCOUNT_ALREADY_ACTIVATED_ERROR:
        case ResultCodes.TOKEN_IS_USED: {
          addNotification({
            message: intl.formatMessage(messages.publicAccountAlreadyActivated),
            actions: (
              <LinkHeap
                target="_blank"
                key="privacyPolicy"
                rel="noreferrer"
                to={acknowledgements.links.contactUs}
                heapEventName={HEAP_EVENTS.upp_click_contactnatera}
                heapEventProps={{
                  location: HeapEventLocation.account_activated_error,
                }}
              >
                {intl.formatMessage(messages.publicContactSupport)}
              </LinkHeap>
            ),
            type: "error",
          });

          break;
        }
        case ResultCodes.ACCOUNT_SUCCESSFULLY_ACTIVATED: {
          addNotification({
            message: intl.formatMessage(
              messages.publicAccountSuccessfullyActivated
            ),
            type: "success",
          });

          break;
        }
        case ResultCodes.USER_IS_DELETED: {
          addNotification({
            message: intl.formatMessage(messages.publicAccountDeleted),
            type: "warning",
            hasTimeout: true,
          });

          break;
        }
        case ResultCodes.EMAIL_VERIFIED:
          {
            addNotification({
              title: intl.formatMessage(
                messages.publicUpdateEmailNotificationTitle
              ),
              message: intl.formatMessage(
                messages.publicUpdateEmailNotification
              ),
              type: "success",
            });
          }
          break;

        case ResultCodes.PASSWORD_UPDATED:
          {
            addNotification({
              title: intl.formatMessage(
                messages.publicResetPasswordNotificationTitle
              ),
              message: (
                <span className="ws-pre-wrap">
                  {intl.formatMessage(messages.publicResetPasswordNotification)}
                </span>
              ),
              type: "success",
            });
          }
          break;
      }
    }
  }, [searchParams, currentLanguage]);

  // Handle old education route redirects (after all hooks)
  if (isOldEducationRoute && redirectEducation) {
    window.location.replace(
      `${config.patientPortal.url}${location.pathname}${location.search}`
    );
    return null;
  }

  if (isOldEducationRoute && !redirectEducation) {
    const publicClinicId = match[1];
    return (
      <Navigate to={routes.patientEducationPage(publicClinicId)} replace />
    );
  }

  return (
    <Main>
      {notifications}
      <RecaptchaRouteGate>
        <Routes>
          <Route path={routes.guestLogin} element={<GuestLogin />} />
          <Route path={routes.billingEstimate} element={<BillingEstimate />} />
          <Route
            path={routes.nonCoveredService}
            element={<NonCoveredService />}
          />
          <Route path={routes.outOfNetwork} element={<OutOfNetwork />} />
          <Route
            path={routes.noLongerAvailable}
            element={<NoLongerAvailable />}
          />
          <Route
            path={routes.insuranceNoLongerAvailable}
            element={<InsuranceNoLongerAvailable />}
          />

          <Route
            path={routes.priorAuthorization}
            element={<PriorAuthorization />}
          />
          <Route
            element={<PostbillGuestLogin />}
            path={routes.postbillGuestLogin}
          />
          <Route
            element={<PostbillBillingCenter />}
            path={routes.billingCenter}
          />
          {/* UPP-6106: public billing entry redirects to sign-up. */}
          <Route
            element={<Navigate to={routes.signUp} replace />}
            path={routes.billingCenterGuestLogin}
          />
          <Route element={<ReviewBills />} path={routes.reviewBills} />
          <Route element={<BillingPayment />} path={routes.billingPayment} />
          <Route element={<BillingFailure />} path={routes.billingFailure} />
          <Route
            element={<BillingConfirmation />}
            path={routes.billingConfirmation}
          />
          <Route
            element={<BillingPaymentPlanDetail />}
            path={routes.billingPaymentPlanDetail}
          />
          <Route
            path={routes.billingPaymentSuccess}
            element={<BillingPaymentSuccess />}
          />
          <Route
            element={<BillingUpdatePayment />}
            path={routes.billingUpdatePayment}
          />
          <Route
            path={routes.billingStatementPdf}
            element={<BillingStatementPdf />}
          />
          <Route element={<UPSGuestLoginPage />} path={routes.upsGuestLogin} />
          <Route
            element={<UPSUpdatePaymentMethodPage />}
            path={routes.upsUpdatePaymentMethod}
          />
          <Route
            element={<UPSPaymentConfirmation />}
            path={routes.upsUpdatePaymentMethodSuccess}
          />
          <Route
            element={<UPSReviewInvoice />}
            path={routes.upsReviewInvoice}
          />
          <Route
            element={
              <RequireUpsSession>
                <UPSCreatePaymentMethod />
              </RequireUpsSession>
            }
            path={routes.upsCreatePaymentMethod}
          />
          <Route
            element={<UPSCreatePaymentConfirmation />}
            path={routes.upsCreatePaymentConfirmation}
          />
          <Route
            element={<UPSCreatePaymentFailure />}
            path={routes.upsCreatePaymentFailure}
          />
          <Route
            element={<UPSNoLongerAvailable />}
            path={routes.upsNoLongerAvailable}
          />
          <Route element={<UPSReceipt />} path={routes.upsReceipt} />
          <Route element={<SendEmail />} path={routes.sendEmail} />
          <Route element={<SetPassword />} path={routes.setPassword} />
          <Route element={<ResetPassword />} path={routes.resetPassword} />
          <Route element={<PromoLanding />} path={routes.promoLanding} />
          <Route element={<Auth />} path={routes.signUpInvite} />
          <Route element={<Auth />} path={routes.loginInInvite} />
          <Route element={<SendEmail />} path={routes.sendEmail} />
          <Route element={<SetPassword />} path={routes.setPassword} />
          <Route element={<ResetPassword />} path={routes.resetPassword} />
          <Route
            element={<ResetPasswordError />}
            path={routes.resetPasswordError}
          />
          <Route element={<Unlock />} path={routes.unlock} />
          <Route element={<Auth />} path={routes.signUp} />
          <Route element={<Activation />} path={routes.activation} />
          <Route element={<Auth />} path={routes.logIn} />
          <Route element={<LoginRedirect />} path={routes.loginRedirect} />
          <Route
            element={<RenewalInterested />}
            path={routes.renewalInterested}
          />
          <Route
            element={<UpdateEmailError />}
            path={routes.updateEmailError}
          />
          <Route
            element={<ScheduleBloodDraw />}
            path={routes.scheduleBloodDraw}
          />
          {/* UPP-6106: public draw-request entry redirects to sign-up. */}
          <Route
            element={<Navigate to={routes.signUp} replace />}
            path={routes.drawRequest}
          />

          {/* Public GIS Entry */}
          <Route element={<PublicGisEntry />} path={routes.publicGisEntry} />
          {/* Anonymous order-lookup (Public_Entry_Order_Lookup) guest funnel */}
          <Route
            element={<PublicGisOrderLookup />}
            path={routes.publicGisOrderLookup}
          />
          {/* Guest ONC intake → custom calendar scheduler. Mounted on the same
            paths the authenticated flow uses so the shared intake step
            components' route-based navigation resolves here for guests too. */}
          <Route element={<GuestGisIntakeLayout />}>
            <Route
              path={`${routes.scheduleAGeneticInformationSessionIntake}/*`}
              element={<GuestIntakeFormRouter />}
            />
            <Route
              path={routes.scheduleAGeneticInformationSessionPatientEducation}
              element={<PatientEducationPage />}
            />
            <Route
              path={
                routes.scheduleAGeneticInformationSessionUnderstandingResults
              }
              element={<GuestUnderstandingResults />}
            />
            <Route
              path={routes.scheduleAGeneticInformationSessionSchedule}
              element={<ScheduleSessionPage />}
            />
          </Route>

          {/* Prenatal Education Routes */}
          <Route element={<PatientEducationProvider />}>
            <Route element={<PatientEducationHeapProvider />}>
              <Route
                element={<PatientEducationLanding />}
                path={routes.patientEducation}
              />
              <Route
                element={<PatientEducationVideoPage videoType="panorama" />}
                path={routes.patientEducationPanorama}
              />
              <Route
                element={<PatientEducationVideoPage videoType="horizon" />}
                path={routes.patientEducationHorizon}
              />
              <Route
                element={<PatientEducationQuestionnaire />}
                path={routes.patientEducationQuestionnaire}
              />
              <Route
                element={<PatientEducationThanks />}
                path={routes.patientEducationCompletion}
              />
            </Route>
          </Route>

          <Route element={<Navigate to={routes.logIn} replace />} path="*" />
        </Routes>
      </RecaptchaRouteGate>
    </Main>
  );
};

export default PublicRouting;
